<script setup lang="ts">
import { computed, ref } from "vue";
import type { ScreenZone } from "@/utils/observation-plan";

/**
 * Calque de zone de sélection du mode association : un trait sur le plan
 * rattache toutes les observations qu'il couvre. Il n'est posé que quand la
 * zone est armée (il intercepte le pan du plan) et rend la main après un
 * trait. Coordonnées en pixels de la surcouche, qu'il recouvre entièrement.
 */

const emit = defineEmits<{
  /** Trait terminé : la zone tracée, ou null si le geste a été annulé. */
  (e: "zone", zone: ScreenZone | null): void;
}>();

const layerEl = ref<HTMLElement | null>(null);
const zone = ref<ScreenZone | null>(null);
/** Doigt (ou stylet) qui trace : les autres contacts sont ignorés. */
let tracingPointerId: number | null = null;

const localPoint = (event: PointerEvent): { x: number; y: number } => {
  const rect = layerEl.value?.getBoundingClientRect();
  return {
    x: event.clientX - (rect?.left ?? 0),
    y: event.clientY - (rect?.top ?? 0),
  };
};

const onDown = (event: PointerEvent): void => {
  // Un second doigt posé pendant le trait (pincement) ne le redémarre pas.
  if (tracingPointerId !== null) return;
  tracingPointerId = event.pointerId;
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  const point = localPoint(event);
  zone.value = { x0: point.x, y0: point.y, x1: point.x, y1: point.y };
};

const onMove = (event: PointerEvent): void => {
  if (!zone.value || event.pointerId !== tracingPointerId) return;
  const point = localPoint(event);
  zone.value = { ...zone.value, x1: point.x, y1: point.y };
};

const finish = (event: PointerEvent, cancelled: boolean): void => {
  if (event.pointerId !== tracingPointerId) return;
  const traced = zone.value;
  tracingPointerId = null;
  zone.value = null;
  // Geste interrompu par le système (paume, geste d'écran) : rien n'est
  // classé — chaque rattachement est enregistré en ligne, sans annulation.
  emit("zone", cancelled ? null : traced);
};

const zoneStyle = computed(() =>
  zone.value
    ? {
        left: `${Math.min(zone.value.x0, zone.value.x1)}px`,
        top: `${Math.min(zone.value.y0, zone.value.y1)}px`,
        width: `${Math.abs(zone.value.x1 - zone.value.x0)}px`,
        height: `${Math.abs(zone.value.y1 - zone.value.y0)}px`,
      }
    : undefined,
);
</script>

<template>
  <div
    ref="layerEl"
    class="zone-layer"
    @pointerdown.stop.prevent="onDown"
    @touchstart.stop.prevent
    @pointermove="onMove"
    @pointerup="finish($event, false)"
    @pointercancel="finish($event, true)"
  >
    <div v-if="zone" class="zone-rect" :style="zoneStyle" />
  </div>
</template>

<style lang="scss" scoped>
.zone-layer {
  position: absolute;
  inset: 0;
  pointer-events: auto;
  cursor: crosshair;
  touch-action: none;
  z-index: 2;
}

.zone-rect {
  position: absolute;
  border: 2px dashed var(--bs-success);
  background: color-mix(in srgb, var(--bs-success) 15%, transparent);
  pointer-events: none;
}
</style>
